/**
 * Manage, Settings (plan docs/plans/lms-plan.md section 13 tab 7 Settings,
 * 5.8, 4.10, 9.1 to 9.3, Appendix A "Settings and jobs"; 18.26, 18.27;
 * WP-48; R4, R23). Replaces WP-03's placeholder in place:
 * tabs/manage/subviews.ts imports this file's DEFAULT export, a component
 * with no props, rendered under the Manage tab's PageHeader and SegmentedTabs
 * at /lms/manage/settings (so this view holds h2 cards, never an h1).
 *
 * Laptop-first two columns (one column below xl):
 * - the settings form (SettingsForm: GET /admin/settings, which creates the
 *   network's row with the 5.8 defaults on the first read, and PUT with the
 *   changed fields);
 * - the status line (StatusPanel: GET /health, the notifications indicator,
 *   storage, the built-in catalog pending count with Install, the last daily
 *   and weekly runs, failed emails) and the run-now card (RunNowPanel: POST
 *   /admin/jobs/run-daily and run-weekly), which refreshes the status line.
 *
 * The two reads are independent: a failed settings read leaves the status
 * line working and the reverse (G10: each failure is said where it happened,
 * with a Retry).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiErrorMessage, getLmsHealth } from '../../../api/client';
import type { LmsHealth } from '../../../api/client';
import { getSettings } from '../../../api/settings';
import type { LmsSettings } from '../../../api/settings';
import { outlineBtnCls } from '../../../../ui/styles';
import { cardCls, ErrorNote } from '../../../components/pickers/shared';
import { RunNowPanel } from './RunNowPanel';
import { SettingsForm } from './SettingsForm';
import { StatusPanel } from './StatusPanel';

export function SettingsView() {
  const [settings, setSettings] = useState<LmsSettings | null>(null);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [health, setHealth] = useState<LmsHealth | null>(null);
  const [healthError, setHealthError] = useState<string | null>(null);
  const [healthLoading, setHealthLoading] = useState(true);
  // Answers for an earlier request (a double refresh) or after unmount are dropped.
  const settingsRequest = useRef(0);
  const healthRequest = useRef(0);

  const loadSettings = useCallback(() => {
    const id = ++settingsRequest.current;
    setSettingsError(null);
    getSettings()
      .then((row) => {
        if (id === settingsRequest.current) setSettings(row);
      })
      .catch((err) => {
        if (id === settingsRequest.current) setSettingsError(apiErrorMessage(err, 'The settings could not be loaded.'));
      });
  }, []);

  const loadHealth = useCallback(() => {
    const id = ++healthRequest.current;
    setHealthLoading(true);
    getLmsHealth()
      .then((h) => {
        if (id !== healthRequest.current) return;
        setHealth(h);
        setHealthError(null);
      })
      .catch((err) => {
        if (id === healthRequest.current) setHealthError(apiErrorMessage(err, 'The Learning Center status could not be read.'));
      })
      .finally(() => {
        if (id === healthRequest.current) setHealthLoading(false);
      });
  }, []);

  useEffect(() => {
    loadSettings();
    loadHealth();
    return () => {
      // Any answer still on its way belongs to a view that is gone.
      settingsRequest.current += 1;
      healthRequest.current += 1;
    };
  }, [loadSettings, loadHealth]);

  return (
    <div className="grid gap-4 xl:grid-cols-3" data-testid="lms-settings">
      <div className="min-w-0 xl:col-span-2">
        {settings ? (
          <SettingsForm settings={settings} onSaved={setSettings} />
        ) : settingsError ? (
          <div className={`${cardCls} space-y-2`}>
            <ErrorNote testId="lms-settings-load-error">{settingsError}</ErrorNote>
            <button type="button" className={outlineBtnCls()} onClick={loadSettings}>
              Retry
            </button>
          </div>
        ) : (
          <div className={`${cardCls} space-y-3`} aria-busy="true" data-testid="lms-settings-loading">
            <div className="h-4 w-1/3 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
            <div className="h-8 w-full animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
            <div className="h-8 w-full animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
          </div>
        )}
      </div>
      <div className="min-w-0 space-y-4">
        <StatusPanel health={health} loading={healthLoading} error={healthError} onRefresh={loadHealth} />
        <RunNowPanel onRan={loadHealth} />
      </div>
    </div>
  );
}

export default SettingsView;
